import { differenceInDays, parseISO, isAfter, addDays } from 'date-fns';
import { paymentService } from '../../firebase/services';
import { autoCreateNotifications } from '../../firebase/notificationService';

// Calculate late fees for overdue payments
export const calculateLateFees = (overduePayments) => {
  let totalLateFees = 0;
  let count = 0;

  overduePayments.forEach(payment => {
    const lateFee = calculateLateFeeForPayment(payment);
    totalLateFees += lateFee.amount;
    count++;
  });

  return {
    total: totalLateFees,
    count: count,
    average: count > 0 ? totalLateFees / count : 0
  };
};

// Calculate late fee for a single payment
export const calculateLateFeeForPayment = (payment) => {
  if (!payment.paymentDate || payment.status !== 'Overdue') {
    return { amount: 0, daysLate: 0, reason: 'Not overdue' };
  }

  const dueDate = new Date(payment.paymentDate);
  const today = new Date();
  const daysLate = differenceInDays(today, dueDate);

  if (daysLate <= 0) {
    return { amount: 0, daysLate: 0, reason: 'Not overdue' };
  }

  // Late fee calculation logic
  let lateFeeAmount = 0;
  let reason = '';

  if (daysLate <= 5) {
    // 5% of original amount for first 5 days
    lateFeeAmount = (payment.amount || 0) * 0.05;
    reason = '5% late fee (1-5 days late)';
  } else if (daysLate <= 15) {
    // 10% of original amount for 6-15 days
    lateFeeAmount = (payment.amount || 0) * 0.10;
    reason = '10% late fee (6-15 days late)';
  } else if (daysLate <= 30) {
    // 15% of original amount for 16-30 days
    lateFeeAmount = (payment.amount || 0) * 0.15;
    reason = '15% late fee (16-30 days late)';
  } else {
    // 20% of original amount for over 30 days
    lateFeeAmount = (payment.amount || 0) * 0.20;
    reason = '20% late fee (over 30 days late)';
  }

  return {
    amount: Math.round(lateFeeAmount * 100) / 100, // Round to 2 decimal places
    daysLate: daysLate,
    reason: reason
  };
};

// Add late fee to a payment
export const addLateFee = async (payment, lateFeeAmount, reason) => {
  try {
    // Validate input
    if (!payment || !payment.id) {
      throw new Error('Invalid payment data');
    }

    if (!lateFeeAmount || lateFeeAmount <= 0) {
      throw new Error('Late fee amount must be greater than 0');
    }

    if (!reason || reason.trim() === '') {
      throw new Error('Reason for late fee is required');
    }

    // Create a new payment record for the late fee
    const lateFeePayment = {
      tenantId: payment.tenantId,
      tenantName: payment.tenantName,
      apartmentId: payment.apartmentId,
      apartmentNumber: payment.apartmentNumber,
      amount: lateFeeAmount,
      paymentType: 'Late Fee',
      paymentMethod: 'System Generated',
      paymentDate: new Date(),
      status: 'Pending',
      description: `Late fee: ${reason}`,
      originalPaymentId: payment.id,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add the late fee payment to the database
    const result = await paymentService.addPayment(lateFeePayment);
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to add late fee payment');
    }

    // Update the original payment to mark it as having a late fee
    const updatedPayment = {
      ...payment,
      hasLateFee: true,
      lateFeeAmount: lateFeeAmount,
      lateFeeReason: reason,
      lateFeeDate: new Date(),
      updatedAt: new Date()
    };

    const updateResult = await paymentService.updatePayment(payment.id, updatedPayment);
    
    if (!updateResult.success) {
      throw new Error(updateResult.error?.message || 'Failed to update original payment');
    }

    // Create notification for the tenant about the late fee
    const lateFeeData = {
      tenantId: payment.tenantId,
      paymentId: payment.id,
      amount: lateFeeAmount,
      reason: reason
    };

    const notificationResult = await autoCreateNotifications.onLateFeeApplied(lateFeeData);
    
    if (notificationResult.success) {
      console.log('Late fee notification created successfully');
    } else {
      console.error('Failed to create late fee notification:', notificationResult.error);
    }

    return {
      success: true,
      lateFeePaymentId: result.id,
      lateFeeAmount: lateFeeAmount,
      message: 'Late fee added successfully'
    };

  } catch (error) {
    console.error('Error adding late fee:', error);
    throw error;
  }
};

// Get all payments with late fees
export const getPaymentsWithLateFees = (payments) => {
  return payments.filter(payment => 
    payment.hasLateFee || 
    payment.paymentType === 'Late Fee' ||
    (payment.status === 'Overdue' && calculateLateFeeForPayment(payment).amount > 0)
  );
};

// Calculate total late fees for a specific tenant
export const calculateTenantLateFees = (payments, tenantId) => {
  const tenantPayments = payments.filter(payment => payment.tenantId === tenantId);
  const lateFeePayments = tenantPayments.filter(payment => 
    payment.paymentType === 'Late Fee' || payment.hasLateFee
  );

  const totalLateFees = lateFeePayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const overduePayments = tenantPayments.filter(payment => payment.status === 'Overdue');
  const potentialLateFees = overduePayments.reduce((sum, payment) => {
    const lateFee = calculateLateFeeForPayment(payment);
    return sum + lateFee.amount;
  }, 0);

  return {
    totalPaid: totalLateFees,
    potential: potentialLateFees,
    count: lateFeePayments.length,
    overdueCount: overduePayments.length
  };
};

// Check if a payment is overdue
export const isPaymentOverdue = (payment) => {
  if (!payment.paymentDate || payment.status === 'Paid') {
    return false;
  }

  const dueDate = new Date(payment.paymentDate);
  const today = new Date();
  
  return isAfter(today, dueDate);
};

// Get next payment due date for a tenant
export const getNextPaymentDueDate = (payments, tenantId) => {
  const tenantPayments = payments.filter(payment => 
    payment.tenantId === tenantId && 
    payment.paymentType === 'Rent' &&
    payment.status !== 'Paid'
  );

  if (tenantPayments.length === 0) {
    return null;
  }

  // Sort by payment date and return the earliest
  const sortedPayments = tenantPayments.sort((a, b) => {
    const dateA = new Date(a.paymentDate);
    const dateB = new Date(b.paymentDate);
    return dateA - dateB;
  });

  return new Date(sortedPayments[0].paymentDate);
};

// Calculate potential late fees for all overdue payments
export const calculatePotentialLateFees = (payments) => {
  const overduePayments = payments.filter(payment => 
    payment.status === 'Overdue' || isPaymentOverdue(payment)
  );

  const potentialFees = overduePayments.map(payment => {
    const lateFee = calculateLateFeeForPayment(payment);
    return {
      paymentId: payment.id,
      tenantName: payment.tenantName,
      apartmentNumber: payment.apartmentNumber,
      originalAmount: payment.amount,
      daysLate: lateFee.daysLate,
      potentialLateFee: lateFee.amount,
      reason: lateFee.reason,
      dueDate: payment.paymentDate
    };
  });

  const totalPotential = potentialFees.reduce((sum, fee) => sum + fee.potentialLateFee, 0);

  return {
    potentialFees: potentialFees,
    totalPotential: totalPotential,
    count: potentialFees.length
  };
};

// Auto-generate late fees for overdue payments (for admin use)
export const autoGenerateLateFees = async (payments) => {
  const overduePayments = payments.filter(payment => 
    payment.status === 'Overdue' && 
    !payment.hasLateFee &&
    isPaymentOverdue(payment)
  );

  const results = [];

  for (const payment of overduePayments) {
    try {
      const lateFee = calculateLateFeeForPayment(payment);
      
      if (lateFee.amount > 0) {
        const result = await addLateFee(
          payment, 
          lateFee.amount, 
          `Auto-generated: ${lateFee.reason}`
        );
        
        results.push({
          paymentId: payment.id,
          success: true,
          lateFeeAmount: lateFee.amount,
          message: result.message
        });
      }
    } catch (error) {
      results.push({
        paymentId: payment.id,
        success: false,
        error: error.message
      });
    }
  }

  return {
    totalProcessed: overduePayments.length,
    successful: results.filter(r => r.success).length,
    failed: results.filter(r => !r.success).length,
    results: results
  };
};

// Get late fee statistics
export const getLateFeeStatistics = (payments) => {
  const lateFeePayments = getPaymentsWithLateFees(payments);
  const overduePayments = payments.filter(p => p.status === 'Overdue');
  
  const totalLateFees = lateFeePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const potentialLateFees = calculatePotentialLateFees(payments).totalPotential;
  
  // Group by month
  const monthlyLateFees = {};
  lateFeePayments.forEach(payment => {
    if (payment.paymentDate) {
      const date = new Date(payment.paymentDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (!monthlyLateFees[monthKey]) {
        monthlyLateFees[monthKey] = { name: monthName, amount: 0, count: 0 };
      }
      monthlyLateFees[monthKey].amount += payment.amount || 0;
      monthlyLateFees[monthKey].count += 1;
    }
  });

  return {
    totalCollected: totalLateFees,
    totalPotential: potentialLateFees,
    collectedCount: lateFeePayments.length,
    overdueCount: overduePayments.length,
    monthlyBreakdown: Object.values(monthlyLateFees).sort((a, b) => {
      const dateA = new Date(a.name);
      const dateB = new Date(b.name);
      return dateA - dateB;
    })
  };
};

// Automated stacking late fees: 5% per 2 weeks overdue, stacking
export const autoStackLateFees = async (payments) => {
  // Only process overdue rent payments
  const overduePayments = payments.filter(
    (p) => p.status === 'Overdue' && p.paymentType === 'Rent'
  );
  const allLateFees = payments.filter((p) => p.paymentType === 'Late Fee');

  for (const payment of overduePayments) {
    const dueDate = new Date(payment.paymentDate);
    const now = new Date();
    if (isNaN(dueDate)) continue;
    // Calculate how many 2-week periods (or partial) overdue
    const daysLate = differenceInDays(now, dueDate);
    if (daysLate < 1) continue; // Only start after 1 day overdue
    const periodsLate = Math.ceil(daysLate / 14); // 14 days = 2 weeks
    const lateFeeAmount = Math.round((payment.amount * 0.05 * periodsLate) * 100) / 100;
    const description = `Late fee: 5% per 2 weeks overdue (Periods: ${periodsLate})`;
    // Find the late fee for this payment (should only be one)
    const lateFee = allLateFees.find((lf) => lf.originalPaymentId === payment.id);
    if (lateFee) {
      // Update the existing late fee if amount or description changed
      if (lateFee.amount !== lateFeeAmount || lateFee.description !== description) {
        await paymentService.updatePayment(lateFee.id, {
          ...lateFee,
          amount: lateFeeAmount,
          description,
          updatedAt: new Date(),
        });
      }
    } else {
      // Create a new late fee payment
      const lateFeePayment = {
        tenantId: payment.tenantId,
        tenantName: payment.tenantName,
        apartmentId: payment.apartmentId,
        apartmentNumber: payment.apartmentNumber,
        amount: lateFeeAmount,
        paymentType: 'Late Fee',
        paymentMethod: 'System Generated',
        paymentDate: new Date(),
        status: 'Pending',
        description,
        originalPaymentId: payment.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await paymentService.addPayment(lateFeePayment);
    }
  }
}; 